import React, { useState } from 'react';
import {
	Dialog,
	DialogTitle,
	DialogContent,
	Grid,
	TextField,
	Button,
	Typography,
	Theme,
} from '@mui/material';
import { WithStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import withStyles from '@mui/styles/withStyles';
import { ResponsiblePersonDetails } from 'common/types';
import { Address } from 'common/modules/atoms/addresses';
import { showAddressFields } from 'common/utils/common';
import { useTranslation } from 'services/localization/useTranslation';

interface OwnProps {
	open: boolean;
	handleClose: () => void;
	responsiblePerson: ResponsiblePersonDetails;
	handleUpdate: (responsiblePerson: ResponsiblePersonDetails) => void;
	shopId: string;
}

type Props = OwnProps & WithStyles<typeof styles>;

const EditResponsiblePersonDialog = (props: Props) => {
	const { t } = useTranslation();
	const [phone, setPhone] = useState<string>(props.responsiblePerson.phone);
	const [email, setEmail] = useState<string>(props.responsiblePerson.email);
	const [homeAddress, setHomeAddress] = useState<Address | null>(
		props.responsiblePerson.homeAddress || null,
	);
	const [accommodation, setAccommodation] = useState<string | null>(
		props.responsiblePerson.accommodation || null,
	);
	const classes = props.classes;

	const handleChange = (event: any) => {
		const target = event.target;
		const value = target.value;
		const name = target.name;

		switch (name) {
			case 'phone':
				setPhone(value);
				break;
			case 'email':
				setEmail(value);
				break;
			case 'accommodation':
				setAccommodation(value);
				break;
			default:
				break;
		}
	};

	const handleHomeAddressChange = (event: any) => {
		const value = event.target.value;
		const name = event.target.name;
		setHomeAddress({
			...homeAddress,
			address: (homeAddress && homeAddress.address) || '',
			[name]: value,
		});
	};

	const handleUpdate = () => {
		const updatedResponsiblePerson: ResponsiblePersonDetails = {
			...props.responsiblePerson,
			phone,
			email,
			...(accommodation && { accommodation }),
			...(homeAddress && { homeAddress }),
		};
		props.handleUpdate(updatedResponsiblePerson);
	};

	return (
		<Dialog open={props.open}>
			<DialogTitle className={classes.title}>
				{t('editResponsiblePersonDialog', 'Change responsible person details')}
			</DialogTitle>
			<DialogContent className={classes.content} data-sl="mask">
				<Grid container spacing={2} justifyContent="space-around">
					<Grid item xs={12}>
						<Typography variant="h5">
							{props.responsiblePerson.firstName} {props.responsiblePerson.lastName}
						</Typography>
					</Grid>
					<Grid item xs={12}>
						<TextField
							variant="standard"
							label={t('common:form.phone')}
							name="phone"
							type="tel"
							value={phone}
							onChange={handleChange}
							fullWidth
						/>
					</Grid>
					<Grid item xs={12}>
						<TextField
							variant="standard"
							label={t('common:form.email')}
							name="email"
							type="email"
							value={email}
							onChange={handleChange}
							fullWidth
						/>
					</Grid>
					{showAddressFields(props.shopId) && (
						<>
							<Grid item xs={12}>
								<TextField
									variant="standard"
									id="address"
									label={t('common:address.homeAddress')}
									name="address"
									value={homeAddress ? homeAddress.address : ''}
									onChange={handleHomeAddressChange}
									type="text"
									fullWidth
								/>
							</Grid>
							<Grid item xs={6}>
								<TextField
									variant="standard"
									id="zipCode"
									label={t('common:address.zipCode')}
									name="zipCode"
									value={homeAddress ? homeAddress.zipCode : ''}
									onChange={handleHomeAddressChange}
									type="text"
									fullWidth
								/>
							</Grid>
							<Grid item xs={6}>
								<TextField
									variant="standard"
									id="city"
									label={t('common:address.city')}
									name="city"
									value={homeAddress ? homeAddress.city : ''}
									onChange={handleHomeAddressChange}
									type="text"
									fullWidth
								/>
							</Grid>
							<Grid item xs={12}>
								<TextField
									variant="standard"
									id="accommodation"
									label={`${t('common:address.accommodation')}`}
									name="accommodation"
									value={accommodation || ''}
									onChange={handleChange}
									fullWidth
								/>
							</Grid>
						</>
					)}
					<Grid item xs={12} className={classes.buttonContainer}>
						<Button variant="text" onClick={props.handleClose}>
							{t('shop.cancel')}
						</Button>
						<Button
							variant="contained"
							color="primary"
							onClick={handleUpdate}
							disabled={!(phone || email)}
						>
							{t('common:actions.update', 'Update')}
						</Button>
					</Grid>
				</Grid>
			</DialogContent>
		</Dialog>
	);
};

const styles = (theme: Theme) =>
	createStyles({
		title: {
			margin: theme.spacing(2),
			textAlign: 'center',
		},
		content: {
			padding: theme.spacing(2),
		},
		buttonContainer: {
			textAlign: 'right',
			'& > *': {
				margin: 5,
			},
			paddingRight: `calc(30px - ${theme.spacing(1)}) !important`,
		},
	});

export default withStyles(styles)(EditResponsiblePersonDialog);
